import React from 'react';
import styled from 'styled-components';

import { Body5, Container, H5, Page } from '@ui';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { handle400Statuses, sendApiDownloadRequest, sendApiRequest } from '@services/api';
import { submitTemplate } from '@services/genaiQueriesV2';
import { includesId, logNetRequest } from '@utils/usefulFuncs';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { Spinner } from '@ui/LoadingSpinner';
import { FlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { FormState } from '@ui/FormState';
import { AI_TOOLS, DOCUMENT_SELECTION_MODES, DOCUMENT_TEMPLATE_MODE_IDS } from '@constants';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { useExtraModeParams } from '@hooks/features/useExtraModeParams';
import { useStreamStatus } from '@hooks/utils/useStreamStatus';
import { convertMonthYearStringToLastDate } from '@utils/usefulFuncs';
import { ExtraModeParams } from './components/ExtraModeParams';
import { DocumentSelectionModalControlled } from '@features/document/DocumentSelectionModal/DocumentSelectionModalControlled';
import { DocumentIcon } from '@assets/icons/svgs/DocumentIcon';
import { CloseButton } from '@ui/buttons/CloseButton';
import { ReportDownloadTool } from './components/DocumentDownloadTool';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 0 !important;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const SelectedDocumentWrapper = styled.div``;
const IconWrapper = styled.div`
  margin-right: 8px;

  svg {
    padding-top: 1px;
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const CloseButtonWrapper = styled.div`
  margin-left: 8px;

  svg {
    padding-top: 1px;
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const TemplateSelectWrapper = styled.div``;
const DocumentsSelectWrapper = styled.div``;

const defaultDocSelectModes = [DOCUMENT_SELECTION_MODES.allCompany, DOCUMENT_SELECTION_MODES.docType];

function SelectedDocument({ doc, handleRemove }) {
  if (!doc) return null;

  const { filename } = doc;
  return (
    <SelectedDocumentWrapper>
      <Container row verticallyCenter>
        <IconWrapper>
          <DocumentIcon />
        </IconWrapper>
        <Container>
          <Body5>{filename}</Body5>
        </Container>
        <CloseButtonWrapper>
          <CloseButton onClick={() => handleRemove(doc)} />
        </CloseButtonWrapper>
      </Container>
    </SelectedDocumentWrapper>
  );
}

const modeIdToUserModeConversion = (modeId) => {
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.portfolio_monitoring_template)
    return AI_TOOLS.PORTFOLIO_MONITORING_TEMPLATE;
  return modeId;
};
const generateHeadingText = (modeId) => {
  if (!modeId) return null;
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.portfolio_monitoring_template) return 'Portfolio Monitoring';
  return 'Download Tool - Mode missing name';
};
const generateDescriptionComponent = (modeId) => {
  if (!modeId) return null;
  return null;
};

function modeParams(modeId) {
  return {};
}

function convertModeParamsToApiFormat(modeParams) {
  const output = {};
  Object.entries(modeParams).forEach(([key, value]) => {
    if (key.includes('_section_header')) {
      return;
    }
    if (['quarter_end_date', 'fiscal_year_end_date'].includes(key)) {
      output[key] = convertMonthYearStringToLastDate(value);
    } else {
      output[key] = value;
    }
    if (key === 'calendar_date') {
      output[key] = convertMonthYearStringToLastDate(value);
    }
  });
  return output;
}

export function TemplateBuilderTool({ onGoBack, modeId, tool }) {
  const advancedSettingsValues = useAdvancedSettings();
  const { model } = advancedSettingsValues;

  const [docSelectModes] = React.useState(defaultDocSelectModes);
  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const [selectedTemplate, setSelectedTemplate] = React.useState('');
  const [availableTemplates, setAvailableTemplates] = React.useState([]);

  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  const [resultId, setResultId] = React.useState(null);
  const streamStatus = useStreamStatus(resultId);
  const { modeExtraParams, state: modeExtraParamsState, onChangeModeExtraParams } = useExtraModeParams(modeId);

  React.useEffect(() => {
    const fetchAvailableTemplates = async () => {
      const { status, data } = await sendApiRequest('GET', 'v1/genai/templates');
      handle400Statuses(status);
      logNetRequest(URL, status, data);
      if (status === 200) {
        setAvailableTemplates(data);
      } else {
        setFormError('Something went wrong, try again');
      }
    };
    fetchAvailableTemplates();
  }, []);

  React.useEffect(() => {
    if (resultId) {
      const id = 'snapshot-answers-section';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [resultId]);

  const handleRemoveDocument = (filenameOrFilenames) => {
    if (Array.isArray(filenameOrFilenames)) {
      setSelectedDocs((prevState) => prevState.filter((d) => !includesId(filenameOrFilenames, d.id)));
    } else {
      setSelectedDocs((prevState) => prevState.filter((d) => d.id !== filenameOrFilenames.id));
    }
  };

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const onSubmit = async () => {
    if (selectedDocs.length === 0) {
      setFormError('Please select at least one document');
      return;
    }

    setFormError(null);
    setIsSubmittingToApi(true);
    setResultId(null);

    const docIds = selectedDocs.map((d) => d.id);
    const settings = { user_mode: modeIdToUserModeConversion(modeId), model, ...modeParams(modeId) };
    addAdvancedSettingValues(advancedSettingsValues, settings);
    const modeParamsBody =
      modeExtraParams.length > 0 ? convertModeParamsToApiFormat({ ...modeExtraParamsState }) : undefined;
    const result = await submitTemplate({
      modeType: modeId,
      templateId: selectedTemplate,
      docIds,
      modeParams: modeParamsBody,
      settings,
    });
    if (result.status === 200) {
      setResultId(result.runId);
    } else {
      setFormError('Something went wrong, try again');
    }
    setIsSubmittingToApi(false);
  };

  const onDownloadDocument = async () => {
    try {
      const filename = 'GeneratedReport';
      const URL = `v1/genai/templates/filled/${modeId}/0?template_run_id=${resultId}&template_id=${selectedTemplate}`;
      const response = await sendApiDownloadRequest(URL);
      const { status, data, error } = response;
      logNetRequest(status, data, URL);
      if (status === 200) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setFormError(error || 'Something went wrong downloading the file, please try again later.');
      }
    } catch (error) {
      setFormError(error || 'Something went wrong downloading the file, please try again later.');
      console.error('Error downloading the file:', error);
    }
  };

  const onClear = () => {
    setSelectedTemplate('');
    setSelectedDocs([]);
    setResultId(null);
    setFormError(null);
  };

  if (resultId) {
    return (
      <Page width={'1800px'}>
        <ReportDownloadTool streamStatus={streamStatus} onDownloadDocument={onDownloadDocument} onClear={onClear} />
      </Page>
    );
  }
  return (
    <PageWrapper>
      <Page width={resultId ? '1800px' : '800px'}>
        <BackArrowButton onClick={onGoBack} />
        <AdvancedSettingsButton tool={tool} />
        <Container top={40}>
          <H5>{generateHeadingText(modeId)}</H5>
          {generateDescriptionComponent(modeId)}
        </Container>
        {isSelectingDocs && (
          <DocumentSelectionModalControlled
            documentSelectionModes={docSelectModes}
            defaultActiveMode={docSelectModes[0]}
            tool={tool}
            selectedDocs={selectedDocs}
            onSelectedDocsChange={setSelectedDocs}
            onDismiss={() => setIsSelectingDocs(false)}
          />
        )}
        {isSubmittingToApi ? (
          <Container top={80} bottom={80} centerAll>
            <Container>
              <Container centerAll>
                <Spinner />
              </Container>
              <Container top={24} left={12}>
                <Body5>Working...</Body5>
              </Container>
            </Container>
          </Container>
        ) : (
          <Container top={8}>
            <FormWrapper>
              <TemplateSelectWrapper>
                <Container top={48}>
                  <Body5 bold>Template</Body5>
                </Container>
                <Container top={16}>
                  <select onChange={(e) => setSelectedTemplate(e.target.value)}>
                    <option value="">Default template</option>
                    {availableTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </Container>
              </TemplateSelectWrapper>
              <DocumentsSelectWrapper>
                <Container top={0}>
                  <Container top={24}>
                    <Body5 bold>Documents to be used for the report</Body5>
                    {selectedDocs.length > 0 ? <Container top={16} /> : null}
                    {selectedDocs.map((d, i) => (
                      <Container key={d.id} top={0} bottom={8}>
                        <SelectedDocument doc={d} handleRemove={handleRemoveDocument} />
                      </Container>
                    ))}
                  </Container>
                  <Container top={24}>
                    {selectedDocs.length > 0 ? (
                      <SkeletonButton onClick={onSelectDocumentsClick}>+ Select Documents</SkeletonButton>
                    ) : (
                      <FlatButton onClick={onSelectDocumentsClick}>+ Select Documents</FlatButton>
                    )}
                  </Container>
                </Container>
              </DocumentsSelectWrapper>
              <Container top={32}>
                <Body5 bold>Parameters</Body5>
              </Container>
              <div style={{ margin: '0px 0 0 0' }}>
                <ExtraModeParams
                  top={24}
                  modeExtraParams={modeExtraParams}
                  modeExtraParamsState={modeExtraParamsState}
                  onChangeModeExtraParams={onChangeModeExtraParams}
                />
              </div>
              <Container top={48}>
                <Container centerAll>
                  <FormState error={formError} />
                </Container>
                <SubmitButtonWrapper>
                  <FlatButton disabled={selectedDocs.length === 0} onClick={onSubmit} fullWidth>
                    Create Report
                  </FlatButton>
                </SubmitButtonWrapper>
              </Container>
            </FormWrapper>
          </Container>
        )}
      </Page>
    </PageWrapper>
  );
}
